import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { getAmenities, createAmenity, deleteAmenity, generateAmenity, updateAmenity } from '@/services/api';
import { Plus, Trash2, Calendar, Dumbbell, Waves, Building2, Trophy, Sparkles, Loader2, Wand2, IndianRupee, Edit2 } from 'lucide-react';
import { toast } from 'sonner';

const AdminAmenities = () => {
  const [amenities, setAmenities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAmenity, setEditingAmenity] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [aiDescription, setAiDescription] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'general',
    available_slots: '',
    price: ''
  });

  useEffect(() => {
    fetchAmenities();
  }, []);

  const fetchAmenities = async () => {
    try {
      const data = await getAmenities();
      setAmenities(data);
    } catch (error) {
      toast.error('Failed to load amenities');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const slotsArray = formData.available_slots
        .split(',')
        .map(slot => slot.trim())
        .filter(slot => slot.length > 0);

      if (slotsArray.length === 0) {
        toast.error('Please add at least one time slot');
        return;
      }

      await createAmenity({
        name: formData.name,
        description: formData.description,
        type: formData.type,
        available_slots: slotsArray,
        price: parseFloat(formData.price) || 0
      });

      toast.success('Amenity created successfully');
      setDialogOpen(false);
      setFormData({ name: '', description: '', type: 'general', available_slots: '', price: '' });
      setAiDescription('');
      fetchAmenities();
    } catch (error) {
      toast.error('Failed to create amenity');
    }
  };

  const handleAIGenerate = async () => {
    if (!aiDescription.trim()) {
      toast.error('Please enter a brief description first');
      return;
    }
    
    setIsGenerating(true);
    try {
      const result = await generateAmenity(aiDescription);
      setFormData({
        name: result.name || '',
        description: result.description || '',
        type: result.type || 'general',
        available_slots: Array.isArray(result.available_slots) 
          ? result.available_slots.join(', ') 
          : result.available_slots || '',
        price: result.price?.toString() || '0'
      });
      toast.success('Amenity details generated by AI!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate amenity details');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleUpdatePrice = async (amenityId, newPrice) => {
    const price = parseFloat(newPrice);
    if (!price || price <= 0) {
      toast.error('Price must be greater than 0');
      return;
    }
    try {
      await updateAmenity(amenityId, { price });
      toast.success('Price updated successfully');
      setEditingAmenity(null);
      fetchAmenities();
    } catch (error) {
      toast.error('Failed to update price');
    }
  };

  const handleDelete = async (amenityId, amenityName) => {
    if (window.confirm(`Are you sure you want to delete "${amenityName}"? This action cannot be undone.`)) {
      try {
        await deleteAmenity(amenityId);
        toast.success('Amenity deleted successfully');
        fetchAmenities();
      } catch (error) {
        toast.error('Failed to delete amenity');
      }
    }
  };

  const getAmenityIcon = (type) => {
    const icons = {
      gym: Dumbbell,
      pool: Waves,
      clubhouse: Building2,
      court: Trophy,
      general: Calendar
    };
    return icons[type] || Calendar;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-amenities">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>Amenities Management</h1>
          <p className="text-sm md:text-base text-slate-600">Add and manage society amenities</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-secondary hover:bg-secondary/90" data-testid="add-amenity-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add New Amenity
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add New Amenity</DialogTitle>
              <DialogDescription>Create a new amenity for residents to book</DialogDescription>
            </DialogHeader>

            {/* AI Amenity Generator Section */}
            <div className="bg-gradient-to-r from-secondary/10 to-accent/10 p-4 rounded-lg border border-secondary/20 mb-4">
              <div className="flex items-center gap-2 mb-3">
                <Wand2 className="h-5 w-5 text-secondary" />
                <span className="font-semibold text-slate-900">AI Smart Amenity Generator</span>
              </div>
              <div className="space-y-3">
                <div>
                  <Input
                    placeholder="Describe the amenity (e.g., 'indoor badminton court', 'rooftop garden')"
                    maxLength={500}
                    value={aiDescription}
                    onChange={(e) => setAiDescription(e.target.value)}
                    data-testid="ai-amenity-input"
                  />
                </div>
                <Button
                  type="button"
                  onClick={handleAIGenerate}
                  disabled={isGenerating || !aiDescription.trim()}
                  className="w-full bg-secondary hover:bg-secondary/90"
                  data-testid="ai-amenity-generate-btn"
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Generating Details...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 mr-2" />
                      Auto-Fill All Fields
                    </>
                  )}
                </Button>
                <p className="text-xs text-slate-500 text-center">AI will fill name, description, type, and suggest time slots</p>
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-slate-500">Review & Submit</span>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="name">Amenity Name</Label>
                <Input
                  id="name"
                  placeholder="e.g., Swimming Pool"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="amenity-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Brief description of the amenity"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  data-testid="amenity-description-input"
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="type">Type</Label>
                <Input
                  id="type"
                  placeholder="e.g., gym, pool, clubhouse, court"
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  required
                  data-testid="amenity-type-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="slots">Available Time Slots</Label>
                <Textarea
                  id="slots"
                  placeholder="Enter time slots separated by commas&#10;e.g., 06:00-08:00, 18:00-20:00, 20:00-22:00"
                  value={formData.available_slots}
                  onChange={(e) => setFormData({ ...formData, available_slots: e.target.value })}
                  required
                  data-testid="amenity-slots-input"
                  rows={3}
                />
                <p className="text-xs text-slate-500">Separate multiple slots with commas</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="price">Price per Booking (₹)</Label>
                <div className="relative">
                  <IndianRupee className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="price"
                    type="number"
                    min="1"
                    step="0.01"
                    placeholder="e.g. 100"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    className="pl-10"
                    data-testid="amenity-price-input"
                  />
                </div>
                <p className="text-xs text-slate-500">Set to 0 for free amenities. Bills are auto-generated on booking.</p>
              </div>
              <Button type="submit" className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3" data-testid="amenity-submit-btn">
                <Plus className="h-5 w-5 mr-2" />
                Add Amenity
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Amenities Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {amenities.length === 0 ? (
          <Card className="border-slate-200 col-span-2">
            <CardContent className="p-12 text-center">
              <p className="text-slate-500">No amenities yet. Add your first amenity!</p>
            </CardContent>
          </Card>
        ) : (
          amenities.map((amenity) => {
            const Icon = getAmenityIcon(amenity.type);
            const isEditing = editingAmenity === amenity.id;
            
            return (
              <Card key={amenity.id} className="border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-testid="amenity-card">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 bg-secondary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Icon className="h-6 w-6 text-secondary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-lg font-semibold text-slate-900">{amenity.name}</h3>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDelete(amenity.id, amenity.name)}
                          data-testid="delete-amenity-btn"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      <p className="text-sm text-slate-600 mb-3">{amenity.description}</p>
                      <div className="flex items-center gap-2 mb-3">
                        <Badge variant="outline">{amenity.type}</Badge>
                        {/* Price Display/Edit */}
                        {isEditing ? (
                          <div className="flex items-center gap-1">
                            <div className="relative">
                              <IndianRupee className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-slate-400" />
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                defaultValue={amenity.price || 0}
                                className="h-8 w-24 pl-6 text-sm"
                                onBlur={(e) => handleUpdatePrice(amenity.id, e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleUpdatePrice(amenity.id, e.target.value);
                                  } else if (e.key === 'Escape') {
                                    setEditingAmenity(null);
                                  }
                                }}
                                autoFocus
                                data-testid={`price-edit-input-${amenity.id}`}
                              />
                            </div>
                          </div>
                        ) : (
                          <Badge 
                            className={`cursor-pointer ${amenity.price > 0 ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                            onClick={() => setEditingAmenity(amenity.id)}
                            data-testid={`price-badge-${amenity.id}`}
                          >
                            <IndianRupee className="h-3 w-3 mr-1" />
                            {amenity.price > 0 ? `${amenity.price.toFixed(2)}` : 'Free'}
                            <Edit2 className="h-3 w-3 ml-1 opacity-50" />
                          </Badge>
                        )}
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 mb-2">Available Slots:</p>
                        <div className="flex flex-wrap gap-2">
                          {amenity.available_slots.map((slot, idx) => (
                            <span key={idx} className="text-xs px-2 py-1 bg-slate-100 rounded-md">
                              {slot}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Stats */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle>Amenities Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="p-4 bg-slate-50 rounded-xl">
              <p className="text-sm text-slate-500 mb-1">Total Amenities</p>
              <p className="text-2xl font-bold text-slate-900">{amenities.length}</p>
            </div>
            <div className="p-4 bg-blue-50 rounded-xl">
              <p className="text-sm text-slate-500 mb-1">Total Time Slots</p>
              <p className="text-2xl font-bold text-blue-700">
                {amenities.reduce((sum, a) => sum + a.available_slots.length, 0)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminAmenities;
